import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { getApiAccessToken } from '../config/authToken';
import { buildCacheKeyFromAxiosConfig, cacheGetResponse, getCachedResponse } from '../offline/httpCache';
import { flushPendingOps, queueOpFromAxiosConfig } from '../offline/pendingSync';
import {
  buildOfflineQueuedResponseData,
  ensurePostPayloadId,
} from '../offline/mutationPayload';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

function generateUuidV4Fallback() {
  // Prefer native randomUUID when available; otherwise fall back to RFC4122-ish v4 using getRandomValues.
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    if (typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // Per RFC4122 v4
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  }
  // Very last-resort fallback (lower quality randomness, but stable shape)
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${s4()}${s4()}-${s4()}-4${s4().slice(1)}-${((8 + Math.random() * 4) | 0).toString(16)}${s4().slice(1)}-${s4()}${s4()}${s4()}`;
}

function ensureClientId(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  if (!payload.id) payload.id = generateUuidV4Fallback();
  return payload;
}

function makeAxiosLikeResponse(config, data, status = 200) {
  return {
    data,
    status,
    statusText: String(status),
    headers: {},
    config,
    request: null,
  };
}

function isOfflineOrNetworkError(error) {
  // axios: if no response => network error / CORS / offline
  return !!error && !error.response;
}

function isApiGet(config) {
  return String(config?.method || '').toLowerCase() === 'get';
}

function isApiMutation(config) {
  const m = String(config?.method || '').toLowerCase();
  return m === 'post' || m === 'put' || m === 'patch' || m === 'delete';
}

function isAuthEndpoint(config) {
  const m = String(config?.method || '').toLowerCase();
  const url = String(config?.url || '');
  if (url === '/users/login' && m === 'post') return true;
  if (url === '/users' && m === 'post') return true; // registration
  if (url === '/users/me' && m === 'delete') return true; // удаление аккаунта — только онлайн
  return false;
}

// Request interceptor for loading states
api.interceptors.request.use(
  (config) => {
    const bearer = getApiAccessToken();
    if (bearer && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${bearer}`;
    }

    // Add loading indicator to global state if needed
    if (window.startGlobalLoading) {
      window.startGlobalLoading('Загрузка...');
    }

    // Best-effort: when we regained connection, flush queued ops in background
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      flushPendingOps().catch(() => {});
    }

    return config;
  },
  (error) => {
    if (window.stopGlobalLoading) {
      window.stopGlobalLoading();
    }
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Stop loading indicator
    if (window.stopGlobalLoading) {
      window.stopGlobalLoading();
    }

    // Cache successful GETs for offline read
    try {
      const cfg = response?.config;
      if (cfg && isApiGet(cfg)) {
        const cacheKey = buildCacheKeyFromAxiosConfig(cfg);
        cacheGetResponse(cacheKey, response.data).catch(() => {});
      }
    } catch {
      // ignore cache errors
    }

    // Офлайн-очередь: показать единый тост (см. App.jsx)
    if (response?.status === 202 && response?.data?.queued) {
      try {
        window.dispatchEvent(new CustomEvent('mad:offlineMutationQueued'));
      } catch {
        /* ignore */
      }
    }

    return response;
  },
  (error) => {
    // Stop loading indicator
    if (window.stopGlobalLoading) {
      window.stopGlobalLoading();
    }

    // Offline-first: serve cached GETs, queue mutations
    try {
      const cfg = error?.config;
      if (cfg && isOfflineOrNetworkError(error)) {
        if (isApiGet(cfg)) {
          const cacheKey = buildCacheKeyFromAxiosConfig(cfg);
          return getCachedResponse(cacheKey).then((cached) => {
            if (cached !== null && cached !== undefined) {
              return makeAxiosLikeResponse(cfg, cached, 200);
            }
            return Promise.reject(error);
          });
        }

        if (isApiMutation(cfg)) {
          if (isAuthEndpoint(cfg)) {
            return Promise.reject(error);
          }
          ensurePostPayloadId(cfg);
          // Queue request to replay later. UI should treat 202 as "queued".
          queueOpFromAxiosConfig(cfg).catch(() => {});
          return Promise.resolve(
            makeAxiosLikeResponse(cfg, buildOfflineQueuedResponseData(cfg), 202)
          );
        }
      }
    } catch {
      // ignore offline handler errors
    }

    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          console.error('Bad Request:', data);
          break;
        case 401:
          console.error('Unauthorized:', data);
          // Redirect to login or show auth error
          break;
        case 403:
          console.error('Forbidden:', data);
          break;
        case 404:
          console.error('Not Found:', data);
          break;
        case 500:
          console.error('Server Error:', data);
          break;
        default:
          console.error('API Error:', data);
      }
    } else if (error.request) {
      // Network error
      console.error('Network Error:', error.message);
    } else {
      // Other error
      console.error('Error:', error.message);
    }

    // Report error to global error handler
    if (window.errorReportingHandler) {
      try {
        window.errorReportingHandler({
          type: 'apiError',
          error: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
          method: error.config?.method,
          timestamp: new Date().toISOString()
        });
      } catch (reportError) {
        // Игнорируем ошибки при отправке отчетов об ошибках
        // Это может происходить из-за расширений браузера
        console.debug('Error reporting failed:', reportError);
      }
    }

    return Promise.reject(error);
  }
);

// Clients
export const getClients = (trainerId) => {
  const params = trainerId ? { trainer_id: trainerId } : {};
  return api.get('/clients', { params });
};
export const createClient = (data) => api.post('/clients', ensureClientId({ ...(data || {}) }));
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id, mode = 'deactivate') =>
  api.delete(`/clients/${id}`, { params: { mode } });
export const getClientsStatistics = (trainerId) => {
  const params = trainerId ? { trainer_id: trainerId } : {};
  return api.get('/clients/statistics', { params });
};
export const updateClientStatus = (id, status) => api.put(`/clients/${id}`, { status });

// Workouts
export const getWorkouts = (params) => api.get('/workouts', { params });
export const createWorkout = (data) => {
  const payload = ensureClientId({ ...(data || {}) });
  const { force_merge, ...workoutData } = payload;
  return api.post('/workouts', force_merge ? payload : workoutData);
};
export const createWorkoutsBatch = (workouts, force_merge = false) => {
  return api.post('/workouts/batch', { workouts, force_merge });
};
export const updateWorkout = (id, data) => api.put(`/workouts/${id}`, data);
export const transferWorkout = (id, data) => api.post(`/workouts/${id}/transfer`, data);
export const deleteWorkout = (id) => api.delete(`/workouts/${id}`);
export const getTodayWorkouts = () => api.get('/today-workouts');

// Server time
export const getServerTime = () => api.get('/server-time');

// Exercises
export const getExercises = (workoutId) => api.get('/exercises', { params: { workout_id: workoutId } });
export const createExercise = (data) => {
  const payload = ensureClientId({ ...(data || {}) });
  return api.post('/exercises', payload);
};
export const updateExercise = (id, data) => api.put(`/exercises/${id}`, data);
export const deleteExercise = (id) => api.delete(`/exercises/${id}`);

// Notes
export const getNotes = (workoutId) => api.get(`/notes/${workoutId}`);
export const createNote = (data) => {
  const payload = ensureClientId({ ...(data || {}) });
  return api.post('/notes', payload);
};

// Custom exercises
export const getCustomExercises = (trainerId) => api.get(`/custom-exercises/${trainerId}`);
export const createCustomExercise = (data) =>
  api.post('/custom-exercises', ensureClientId({ ...(data || {}) }));
export const updateCustomExercise = (id, data) => api.put(`/custom-exercises/${id}`, data);
export const deleteCustomExercise = (id) => api.delete(`/custom-exercises/${id}`);

// Trainer settings
export const getHiddenExercises = (trainerId) => api.get(`/trainer-settings/${trainerId}/hidden-exercises`);
export const updateHiddenExercises = (trainerId, hiddenExercises) => 
  api.put(`/trainer-settings/${trainerId}/hidden-exercises`, { hiddenExercises });

// Unified exercises (standard + custom)
export const getAllExercises = (trainerId) => api.get(`/exercises/${trainerId}`);

// Client specific
export const getClientWorkouts = (clientId) => api.get(`/client/${clientId}/workouts`);
export const getClientBalance = (clientId) => api.get(`/clients/${clientId}/balance`);
export const getClientPayments = (clientId, params) =>
  api.get(`/clients/${clientId}/payments`, { params });
export const getClientExercises = (clientId) => api.get(`/client/${clientId}/exercises`);
export const getClientExerciseHistory = (clientId, exerciseName) =>
  api.get(`/client/${clientId}/exercises/${encodeURIComponent(exerciseName)}`);
export const getClientExerciseWorkingWeights = (clientId, exerciseNames) => {
  const params = {};
  if (Array.isArray(exerciseNames) && exerciseNames.length > 0) {
    // Send raw names; axios will URL-encode querystring.
    // Backend applies decodeURIComponent safely (no-op for non-encoded strings).
    params.names = exerciseNames.join(',');
  }
  return api.get(`/client/${clientId}/exercise-working-weights`, { params });
};
export const updateClientExerciseWorkingWeight = (clientId, exerciseName, manualWorkingWeight) =>
  api.put(
    `/client/${clientId}/exercise-working-weights/${encodeURIComponent(exerciseName)}`,
    { manual_working_weight: manualWorkingWeight }
  );
export const updateExerciseSet = (id, data) => api.put(`/exercise-sets/${id}`, data);
export const deleteExerciseSet = (id) => api.delete(`/exercise-sets/${id}`);
export const cancelWorkout = (id) => api.put(`/workouts/${id}`, { status: 'cancelled' });
/** Массовая отмена — один HTTP-запрос и один пересчёт баланса на клиента на сервере */
export const bulkCancelWorkouts = (ids) => api.post('/workouts/bulk-cancel', { ids });

// Users
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const completeTelegramOnboarding = (data) => api.post('/auth/telegram/onboarding', data);
export const getUser = (id) => api.get(`/users/${id}`);
export const deleteMyAccount = () => api.delete('/users/me');

// Account sync
export const generateSyncCode = () => api.post('/auth/sync/generate-code');
export const verifySyncCode = (targetUserId, syncCode) =>
  api.post('/auth/sync', { targetUserId, syncCode });
export const confirmSync = (targetUserId, syncCode, keepUserId, mergeData = false) =>
  api.post('/auth/sync/confirm', { targetUserId, syncCode, keepUserId, mergeData });

// Conflict validation
export const validateWorkoutConflict = (data) => api.post('/workouts/validate-conflict', data);

// Training Programs
export const getTrainingPrograms = (trainerId) => api.get(`/training-programs/${trainerId}`);
export const createTrainingProgram = (data) =>
  api.post('/training-programs', ensureClientId({ ...(data || {}) }));
export const updateTrainingProgram = (id, data) => api.put(`/training-programs/${id}`, data);
export const deleteTrainingProgram = (id) => api.delete(`/training-programs/${id}`);

// Analytics
export const getTrainerAnalytics = (trainerId, params) => api.get(`/trainers/${trainerId}/analytics`, { params });

// Trainer Locations
export const getTrainerLocations = (trainerId) => api.get(`/trainer-locations/${trainerId}`);
export const createTrainerLocation = (data) =>
  api.post('/trainer-locations', ensureClientId({ ...(data || {}) }));
export const updateTrainerLocation = (id, data) => api.put(`/trainer-locations/${id}`, data);
export const deleteTrainerLocation = (id, data) => api.delete(`/trainer-locations/${id}`, { data });

// Payments (axios — офлайн-очередь и кеш как у остальных мутаций)
export const createPayment = (data) => api.post('/payments', ensureClientId({ ...(data || {}) }));
export const updatePayment = (id, data) => api.put(`/payments/${id}`, data);
export const deletePayment = (id) => api.delete(`/payments/${id}`);

export default api;
